<!--
  File name: FilterAsir.vue
  Program Description:
    - This component allows you to filter the ASIR data.
    - The filtered data are used for the ASIR Reports
    generation page.
-->
<template>
  <v-container>
    <template v-if="yearOnly">
      <v-row>
        <v-col
          cols="5"
          class="py-8"
        >
          <v-autocomplete
            v-model="year"
            dense
            outlined
            clearable
            hide-details
            multiple
            label="Year"
            :items="years"
            @input="filterByYear"
          />
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <v-card>
        <v-card-title>
          Filters
        </v-card-title>
        <v-card-text>
          <span>Year</span>
          <v-autocomplete
            v-model="year"
            dense
            outlined
            clearable
            hide-details
            multiple
            class="py-3"
            label="Year"
            :items="years"
          />
          <span>Sector</span>
          <v-autocomplete
            v-model="sector"
            dense
            outlined
            multiple
            clearable
            hide-details
            class="py-3"
            item-text="value"
            item-value="_id"
            label="Sector"
            :items="sectors"
            @input="val => val && (val.length === 0) && (subsector = [])"
          />
          <v-autocomplete
            v-model="subsector"
            dense
            outlined
            multiple
            clearable
            hide-details
            class="py-3"
            item-text="value"
            item-value="_id"
            label="Subsector"
            :items="subsectors"
            :disabled="!subsectors.length"
          />
          <span>Classification</span>
          <v-row>
            <v-col>
              <v-checkbox
                v-model="classification.government"
                label="Government"
                @click="classification.private = false"
              />
            </v-col>
            <v-col>
              <v-checkbox
                v-model="classification.private"
                label="Private"
                @click="classification.government = false"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import crud from '@/utils/rest/crud'
export default {
  name: 'FilterAsir',
  props: {
    value: {
      type: [Number, Array, Object],
      default: undefined
    },
    yearOnly: {
      type: Boolean,
      default: false
    },
    asirs: {
      type: Array,
      default: undefined
    },
    defaultYear: {
      type: Number,
      default: 0
    }
  },
  data: () => ({
    year: [],
    sector: [],
    subsector: [],
    classification: {
      government: false,
      private: false
    },
    sectors: [],
    init: false
  }),
  computed: {
    years () {
      if (!this.asirs.length) return []
      const years = this.asirs.map(item => parseInt(item.year))
      this.defaultYear && years.push(this.defaultYear)
      return [...new Set(years)].sort((a, b) => b - a)
    },
    subsectors () {
      const sector = this.sectors.filter(sector => this.sector.includes(sector._id))
      if (!sector.length) return []
      const subsectors = []
      sector.forEach(item => subsectors.push(...item.subsector))
      return subsectors
    },
    modelValue: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    year: function (val) {
      this.$emit('yearChange', val)
    }
  },
  async created () {
    if (this.defaultYear) {
      this.year = [this.defaultYear]
      this.filterByYear()
    }
    if (this.yearOnly) return
    await this.getSectors()
    this.init = true
  },
  // For every input, the component will return filtered ASIR data
  updated () {
    this.$emit('update:sectors', this.sector)
    if (!this.init || this.yearOnly) return
    const classType = this.classification
    const unfiltered = !this.year.length && !this.sector.length && !this.subsector.length && !(classType.government || classType.private)
    if (unfiltered) return (this.modelValue = this.asirs)
    const filterBy = {
      year: (asir) => this.year.includes(parseInt(asir.year)),
      sector: (asir) => this.sector.includes(asir.sectorName._id),
      subsector: (asir) => asir.subSectorName[0] && this.subsector.includes(asir.subSectorName[0]._id),
      classification: (asir) => {
        const [selectedType] = Object.keys(classType).filter(key => classType[`${key}`])
        return asir.sectorName.classification === selectedType
      }
    }
    const filteredValue = [...this.asirs].filter(asir => {
      return (this.year.length ? filterBy.year(asir) : true) &&
      (this.sector.length ? filterBy.sector(asir) : true) &&
      (this.subsector.length ? filterBy.subsector(asir) : true) &&
      (this.classification.government || this.classification.private
        ? filterBy.classification(asir) : true)
    })
    this.modelValue = filteredValue
  },
  methods: {
    // Get current sectors from master data
    async getSectors () {
      const response = await crud('sector')
        .list({
          count: 10000,
          advancedQuery: { type: 'sector' }
        })
      this.sectors = response.entries
    },
    // Filter ASIR data by selected year
    filterByYear () {
      const filteredValue = [...this.asirs].filter(asir => this.year.includes(parseInt(asir.year)))
      if (!this.year.length) this.modelValue = this.asirs
      else this.modelValue = filteredValue
    }
  }
}
</script>
